import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { ContactInfoPopupComponent } from '../../components/contact-info-popup/contact-info-popup.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { DeleteConfirmationComponent } from '../../components/delete-confirmation/delete-confirmation.component';
import { EAssessmentSubTypes, EAssessmentTypes, ESectionTypes } from '../../../shared/models/eop_types.enum';
import { Icons } from '../../../shared/helpers/icons';
import { IContact, IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { ContactsService } from '../../../shared/services/contact.service';
import { IAssessment } from '../../../shared/models/assessment';
import { HazardAnnexesService } from '../../../shared/services/hazardAnnexes.service';
import { SendAlAssessmentComponent } from "../../hazard-annexes/send-al-assessment/send-al-assessment.component";

@Component({
  selector: 'staff-skills-survey',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    EopAssistantComponent,
    PrvNextButtonsComponent,
    ContactInfoPopupComponent,
    PopupMessageComponent,
    SpinnerComponent,
    IntroductionComponent,
    DeleteConfirmationComponent,
    SendAlAssessmentComponent
],
  templateUrl: './staff-skills-survey.component.html',
  styleUrl: './staff-skills-survey.component.css'
})
export class StaffSkillsSurveyComponent implements OnInit {
  
  sectionType = ESectionTypes['Staff Skills Survey & Inventory'];

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  searchQuery: string = '';
  selectedContact: IContact | null = null;
  fullContactsList: IContact[] = [];
  contactsList: IContact[] = [];
  fullStaffSkillsSurveysList: IAssessment[] = [];
  staffSkillsSurveysList: IAssessment[] = [];
  messageModal: string | null = null;
  popupMessage: string = '';
  showModalType: string = '';
  formData: Record<string, boolean | string> = {};
  comments: string = '';
  formFields: string[] = [
    'AED',
    'Assistant Principal',
    'Bi/Multi-lingual',
    'Bus/Truck Driver',
    'CB Radio',
    'Construction (electrical, plumbing, carpentry, etc.)',
    'CPR',
    'Trained to assist individuals with disabilities and access/functional needs',
    'Emergency Diabetic Medications',
    'Emergency Management',
    'Emergency Planning',
    'Epi Pen',
    "Erin's Law: Child Sexual Abuse Certification",
    'Finance/Bookkeeping',
    'Firefighting',
    'First Aid',
    'Food Preparation',
    'Ham Radio Operator',
    'Incident Reporting Procedures',
    'Individual Emergency Action Plans (IEAP)',
    'Jason Flatt Suicide Prevention Training',
    'Law Enforcement',
    'Mechanical Ability',
    'Medical Emergency Management',
    'MERT Team member',
    'Principal',
    'Public Information and/or Journalism',
    'Recreation Leader',
    'Registered Nurse / Nurse Practitioner /Triage /First Aid/ crisis counseling',
    'Running/Jogging',
    'Search and Rescue',
    'Shelter Management',
    'Structural Engineering',
    'Superintendent',
    'Survival Training and Techniques',
    'Technology Coordinator',
    'Triage',
    'Utilities Management',
    'Waste Disposal',
    'Other'
  ];
  newCustomSkill: string = '';
  customSkills: string[] = [];

  loading: boolean = false;
  loadingContacts: boolean = true;
  showPlanningTeamModal: boolean = false;

  constructor(
    private userDataService: UserDataService,
    private contactsService: ContactsService,
    private hazardAnnexesService: HazardAnnexesService
  ) { }

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadContacts();
  };

  private initializeEmptyForm(): void {
    this.formData = {};
    this.formFields.forEach((field) => (this.formData[field] = false));
    this.formData['comments'] = '';
  }

  // load all Contacts and Contacts of a department
  loadContacts(): void {
    this.loading = true;
    this.contactsService.getContacts(this.currentBuildingId!).subscribe({
      next: (response) => {
        this.loadingContacts = false;
        this.loading = false;
        this.fullContactsList = response.data || [];
        this.contactsList = [...this.fullContactsList];
        console.log('Contacts loaded successfully:', response);
        // Sort the list after retrieving contacts list.
        if (this.contactsList.length > 0) {
          this.contactsList.sort((a, b) => a.name.localeCompare(b.name));
          this.selectedContact = this.contactsList[0];
        }
        this.loadAllStaffSkillsSurveys();
      },
      error: (error: any) => {
        this.loading = false;
        this.loadingContacts = false;
        console.error('Error loading contacts:', error.message);
      }
    });
  };

  loadAllStaffSkillsSurveys(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.hazardAnnexesService.getALAssessments(this.currentBuildingId, String(EAssessmentTypes["AL-Skills Survey & Inventory"])).subscribe({
        next: (response) => {
          this.loading = false;
          this.fullStaffSkillsSurveysList = response.data || [];
          this.staffSkillsSurveysList = [...this.fullStaffSkillsSurveysList];
          console.log('Survey loaded successfully:', response);
          if (this.selectedContact) {
            this.prefillFormForContact(this.selectedContact.id);
          }
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error loading contacts:', error.message);
        }
      });
    }
  }

  private prefillFormForContact(contactId: number): void {
    this.initializeEmptyForm();
  
    const matchedSurvey = this.fullStaffSkillsSurveysList.find(
      (s) => s.contact_id === contactId
    );
  
    if (matchedSurvey?.response_data) {
      this.customSkills = [];

      Object.entries(matchedSurvey.response_data).forEach(([key, value]) => {
        if (key === 'comments') {
          this.formData['comments'] = value as string;
        } 
        else if (this.formFields.includes(key)) {
          // Predefined skill
          this.formData[key] = value === 'Yes';
        } 
        else {
          // Custom skill detected
          this.customSkills.push(key);
          this.formData[key] = value === 'Yes';
        }
      });
    }
  
    console.log('Prefilled form data:', this.formData);
  }

  onContactChange(contact: IContact): void {
    this.selectedContact = contact
    this.prefillFormForContact(contact.id);
  }

  filterContacts(): void {
    const query = this.searchQuery.toLowerCase().trim();
  
    this.contactsList = this.fullContactsList.filter((item) => {
      return (
        item.name.toLowerCase().includes(query) ||
        item.Title?.toLowerCase().includes(query) ||
        item.workphone?.toLowerCase().includes(query) ||
        item.email?.toLowerCase().includes(query)
      );
    });
  }

  handleOpenModal(modalName: string, selectedItem?: any): void {
    this.showModalType = modalName;
  }

  handleCloseModal(modalName?: string): void {
    this.showModalType = '';
  }

  handleSave(message: {type: string, message: string}, actionType: string): void {
    this.loadContacts();
    this.handleCloseModal(actionType);

    if (message.type === 'success') {
      this.messageModal = 'success';
      this.popupMessage = message.message;
      
    } else if (message.type === 'unsuccess') {
      this.messageModal = 'unsuccess';
      this.popupMessage = message.message;
    } 
  }
  
  addCustomSkill(): void {
    const skill = this.newCustomSkill.trim();
  
    if (!skill) return;
    if (this.customSkills.includes(skill) || this.formFields.includes(skill)) {
      alert('This skill already exists.');
      return;
    }
    
    this.customSkills.push(skill);
    this.formData[skill] = true;
    this.newCustomSkill = '';
  
    console.log('Added new skill:', skill);
  }
  
  removeUncheckedCustomSkills(): void {
    this.customSkills = this.customSkills.filter(skill => this.formData[skill]);
  }

  saveStaffSkill(): void {
    if (this.currentBuildingId && this.selectedContact) {
      const result: Record<string, string> = {};
  
      for (const key in this.formData) {
        const value = this.formData[key];
        if (key === 'Other') continue;
        if (key === 'comments' && value) result[key] = String(value);
        else if (value === true) result[key] = 'Yes';
      }
      
      const payload = { 
        "contact_id": this.selectedContact.id,
        "receiver": this.selectedContact.email,
        "response_data": result 
      };
      console.log('Form data to be saved:', payload);
      this.hazardAnnexesService.updateALAssessment(this.currentBuildingId, String(EAssessmentTypes["AL-Skills Survey & Inventory"]), String(EAssessmentSubTypes["AL-Skills Survey & Inventory"]), payload).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.loadAllStaffSkillsSurveys();
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating Staff Skills Survey & Inventory.';
          }
        },
        error: (error: any) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      })
    }
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = '';
  }


}
